/**
 * Engagement traffic: poll votes and feedback.
 *
 * Every call fails soft. A poll that cannot reach the server still records the
 * attendee's vote on the device and says so, rather than erroring in front of
 * them at a venue where the network is not to be trusted. None of these carry
 * anything personal — `deviceId` is a random string generated on first launch.
 */

export interface PollResults {
  counts: number[];
  total: number;
}

/**
 * Tiny stable string hash. A poll's identity is its widget id plus a hash of its
 * options, so editing the options starts a fresh poll instead of re-labelling
 * votes already cast. Lives here so the app and the admin compute the same key.
 */
export function hashText(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** The key a poll's votes are stored under. */
export function pollKey(widgetId: string, options: string[]): string {
  return `${widgetId}:${hashText(options.join('|'))}`;
}

export interface FeedbackPayload {
  /** The widget that collected it, so results roll up per widget. */
  widgetId: string;
  targetKind: 'event' | 'session';
  targetId: string;
  rating: number;
  scale: number;
  comment?: string;
  deviceId: string;
}

const base = (u: string) => u.replace(/\/$/, '');

export async function submitVote(
  cms: string, tenantId: string, pollId: string, deviceId: string, option: number,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  if (!cms) return false;
  try {
    const res = await fetchImpl(`${base(cms)}/tenants/${tenantId}/polls/${encodeURIComponent(pollId)}/vote`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ deviceId, option }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchPollResults(
  cms: string, tenantId: string, pollId: string, fetchImpl: typeof fetch = fetch,
): Promise<PollResults | null> {
  if (!cms) return null;
  try {
    const res = await fetchImpl(`${base(cms)}/tenants/${tenantId}/polls/${encodeURIComponent(pollId)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as PollResults;
    return Array.isArray(data.counts) ? data : null;
  } catch {
    return null;
  }
}

export async function submitFeedback(
  cms: string, tenantId: string, payload: FeedbackPayload, fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  if (!cms) return false;
  try {
    const res = await fetchImpl(`${base(cms)}/tenants/${tenantId}/feedback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Anonymous per-install id. Random, not derived from anything about the person. */
export function newDeviceId(): string {
  const r = () => Math.random().toString(36).slice(2, 10);
  return `d-${r()}${r()}`;
}
